import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  MenuItem,
  Paper,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import useApi from "../../../hooks/useApi";
import { useToast } from "../../../contexts/ToastContext";
import { AuthContext } from "../../../Auth/AuthProvider";
import { VerifyPermission } from "../../../Funcoes/VerifyPermission";
import { useRegisterCadastrosPageActions } from "../layout";
import { STATUS_COLORS, STATUS_LABELS, formatDateTime } from "../shared/status";
import { PERM_SOLICITAR } from "../shared/permissions";

const PAGE_SIZE = 25;

const filtrosIniciais = {
  status: "",
  texto: "",
  data_inicio: "",
  data_fim: "",
};

/**
 * @param {{ statusFixo?: string, titulo?: string }} props
 */
const SolicitacoesLista = ({ statusFixo = "", titulo = "Solicitações de produtos" }) => {
  const api = useApi();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { notifyError } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const podeSolicitar = VerifyPermission(user, PERM_SOLICITAR);

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [dados, setDados] = useState([]);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [filtros, setFiltros] = useState({ ...filtrosIniciais, status: statusFixo });
  const [filtrosDraft, setFiltrosDraft] = useState({ ...filtrosIniciais, status: statusFixo });
  const [filterOpen, setFilterOpen] = useState(false);

  const fetchDados = async (cur = filtros, pageIndex = page) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", String(pageIndex + 1));
      params.append("limit", String(PAGE_SIZE));
      const status = statusFixo || cur.status;
      if (status) params.append("status", status);
      if (cur.texto) params.append("texto", cur.texto);
      if (cur.data_inicio) params.append("data_inicio", cur.data_inicio);
      if (cur.data_fim) params.append("data_fim", cur.data_fim);

      const { data } = await api.get(
        `Painel/API/Cadastros/Produtos/Solicitacoes/index.php?${params.toString()}`
      );
      setDados(data.data || []);
      setTotal(data.quantidade || 0);
    } catch (e) {
      notifyError(e?.response?.data?.message || "Erro ao carregar solicitações.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(0);
    setFiltros({ ...filtrosIniciais, status: statusFixo });
    setFiltrosDraft({ ...filtrosIniciais, status: statusFixo });
  }, [statusFixo]);

  useEffect(() => {
    fetchDados(filtros, page);
  }, [statusFixo, page]);

  const refreshData = async () => {
    if (refreshing) return;
    setRefreshing(true);
    await fetchDados(filtros, page);
    setRefreshing(false);
  };

  useRegisterCadastrosPageActions({
    onRefresh: refreshData,
    onFilter: statusFixo
      ? undefined
      : () => {
          setFiltrosDraft(filtros);
          setFilterOpen(true);
        },
    refreshing,
  });

  const openDetail = (id) => navigate(`/modulo/cadastros/produtos/${id}`);

  const aplicarFiltros = (nextFiltros) => {
    setFiltros(nextFiltros);
    if (page !== 0) {
      setPage(0);
    } else {
      fetchDados(nextFiltros, 0);
    }
  };

  const pagination = (
    <TablePagination
      component="div"
      count={total}
      page={page}
      onPageChange={(_, next) => setPage(next)}
      rowsPerPage={PAGE_SIZE}
      rowsPerPageOptions={[PAGE_SIZE]}
      labelRowsPerPage="Por página"
      labelDisplayedRows={({ from, to, count }) =>
        `${from}–${to} de ${count !== -1 ? count : `mais de ${to}`}`
      }
      sx={{
        borderTop: "1px solid",
        borderColor: "divider",
        flexShrink: 0,
        ".MuiTablePagination-toolbar": { minHeight: 48, px: 1 },
      }}
    />
  );

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2 },
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
      }}
    >
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }}
        spacing={1.5}
      >
        <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
          {titulo}
        </Typography>
        {!statusFixo && podeSolicitar && (
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddOutlinedIcon />}
            onClick={() => navigate("/modulo/cadastros/produtos/nova")}
            sx={{ minHeight: 44, width: { xs: "100%", sm: "auto" } }}
          >
            Nova solicitação
          </Button>
        )}
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6, flex: 1 }}>
          <CircularProgress size={28} />
        </Box>
      ) : isMobile ? (
        <Box sx={{ flex: 1, overflow: "auto", minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
            {dados.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  Nenhuma solicitação encontrada.
                </Typography>
              </Paper>
            ) : (
              <Stack spacing={1}>
                {dados.map((row) => (
                  <Paper
                    key={row.id}
                    variant="outlined"
                    onClick={() => openDetail(row.id)}
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      cursor: "pointer",
                      "&:active": { transform: "scale(0.99)" },
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={1}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                          #{row.id}
                        </Typography>
                        <Typography variant="body2" fontWeight={700} sx={{ mt: 0.25 }}>
                          {row.descricao}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {row.solicitante_nome || row.solicitante_email || "—"} ·{" "}
                          {formatDateTime(row.created_at)}
                        </Typography>
                        {(row.b1_cod || row.b1_cod_similar) && (
                          <Typography variant="caption" fontWeight={600} display="block" sx={{ mt: 0.5 }}>
                            {row.b1_cod || row.b1_cod_similar}
                          </Typography>
                        )}
                      </Box>
                      <Chip
                        size="small"
                        label={STATUS_LABELS[row.status] || row.status}
                        color={STATUS_COLORS[row.status] || "default"}
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
          </Box>
          {pagination}
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
          }}
        >
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Solicitante</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>UM</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell>Criado em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nenhuma solicitação encontrada.
                    </TableCell>
                  </TableRow>
                ) : (
                  dados.map((row) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => openDetail(row.id)}
                    >
                      <TableCell>{row.id}</TableCell>
                      <TableCell>{row.descricao}</TableCell>
                      <TableCell>{row.solicitante_nome || row.solicitante_email || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={STATUS_LABELS[row.status] || row.status}
                          color={STATUS_COLORS[row.status] || "default"}
                        />
                      </TableCell>
                      <TableCell>{row.unidade_medida || "—"}</TableCell>
                      <TableCell>{row.b1_cod || row.b1_cod_similar || "—"}</TableCell>
                      <TableCell>{formatDateTime(row.created_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {pagination}
        </Paper>
      )}

      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Filtros</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Status"
              value={filtrosDraft.status}
              onChange={(e) => setFiltrosDraft((f) => ({ ...f, status: e.target.value }))}
              fullWidth
            >
              <MenuItem value="">Todos</MenuItem>
              {Object.entries(STATUS_LABELS).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Busca (descrição, solicitante, código)"
              value={filtrosDraft.texto}
              onChange={(e) => setFiltrosDraft((f) => ({ ...f, texto: e.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                type="date"
                label="Data início"
                InputLabelProps={{ shrink: true }}
                value={filtrosDraft.data_inicio}
                onChange={(e) => setFiltrosDraft((f) => ({ ...f, data_inicio: e.target.value }))}
                fullWidth
              />
              <TextField
                type="date"
                label="Data fim"
                InputLabelProps={{ shrink: true }}
                value={filtrosDraft.data_fim}
                onChange={(e) => setFiltrosDraft((f) => ({ ...f, data_fim: e.target.value }))}
                fullWidth
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              const limpo = { ...filtrosIniciais, status: statusFixo };
              setFiltrosDraft(limpo);
              setFilterOpen(false);
              aplicarFiltros(limpo);
            }}
          >
            Limpar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setFilterOpen(false);
              aplicarFiltros(filtrosDraft);
            }}
          >
            Aplicar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SolicitacoesLista;
